import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * MVP upsell-to-product mapping.
 * Keys = product handles that trigger the upsell.
 * Values = product handle to upsell.
 * Migrate to metafields later for admin control.
 */
const UPSELL_MAP = {
  // Example: when customer adds "deodorant", upsell "body-wash"
  // 'deodorant': 'body-wash',
  // 'body-lotion': 'hand-cream',
};

/**
 * Post-add-to-cart upsell modal.
 * Intercepts CartAddEvent, checks the mapping, fetches the upsell
 * product data, and shows a modal before opening the cart drawer.
 */
class UpsellModalComponent extends Component {
  #dialog = null;
  #upsellVariantId = null;
  #pendingDrawerOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#dialog = this.querySelector('dialog');
    this.closeBtn = this.querySelector('[ref="closeBtn"]');
    this.addBtn = this.querySelector('[ref="addBtn"]');
    this.skipBtn = this.querySelector('[ref="skipBtn"]');
    this.headline = this.querySelector('[ref="headline"]');
    this.imageWrap = this.querySelector('[ref="imageWrap"]');
    this.description = this.querySelector('[ref="description"]');
    this.swatchesWrap = this.querySelector('[ref="swatchesWrap"]');
    this.badge = this.querySelector('[ref="badge"]');

    this.closeBtn?.addEventListener('click', this.#onSkip);
    this.skipBtn?.addEventListener('click', this.#onSkip);
    this.addBtn?.addEventListener('click', this.#onAdd);
    this.#dialog?.addEventListener('close', this.#onDialogClose);

    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  /**
   * Check if the added product has an upsell mapping.
   * If yes, intercept and show modal. If no, let the drawer open normally.
   */
  #onCartAdd = async (event) => {
    // Give a small delay for the cart state to settle
    await new Promise((r) => setTimeout(r, 100));

    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      if (!cart.items || cart.items.length === 0) return;

      // Check the most recently added item (first in array)
      const lastItem = cart.items[0];
      const handle = lastItem.handle;

      const upsellHandle = UPSELL_MAP[handle];
      if (!upsellHandle) return; // No upsell — drawer opens normally

      // Prevent the cart drawer from auto-opening
      this.#pendingDrawerOpen = true;

      // Signal to cart-drawer.js that we're intercepting
      document.dispatchEvent(new CustomEvent('upsell:intercepting'));

      // Fetch upsell product
      const root = this.dataset.routesRoot || '/';
      const productRes = await fetch(`${root}products/${upsellHandle}.json`);
      if (!productRes.ok) {
        this.#openDrawer();
        return;
      }

      const { product } = await productRes.json();
      this.#populateModal(product);
      this.#showModal();
    } catch {
      this.#openDrawer();
    }
  };

  #populateModal(product) {
    const variant = product.variants?.[0];
    this.#upsellVariantId = variant?.id;

    const price = variant
      ? (variant.price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '';

    const comparePrice = variant?.compare_at_price
      ? (variant.compare_at_price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '';

    // Headline
    if (this.headline) {
      const titleUpper = product.title.toUpperCase();
      this.headline.innerHTML = comparePrice
        ? `ADD ${titleUpper} FOR <s>${comparePrice}</s> ${price}`
        : `ADD ${titleUpper} FOR ${price}`;
    }

    // Image
    if (this.imageWrap && product.image) {
      this.imageWrap.innerHTML = `<img src="${product.image.src}" alt="${this.#escapeHtml(product.title)}" loading="lazy">`;
    }

    // Description
    if (this.description) {
      // Strip HTML from body_html for plain text
      const tmp = document.createElement('div');
      tmp.innerHTML = product.body_html || '';
      const plainText = tmp.textContent || '';
      this.description.textContent = plainText.length > 200 ? plainText.slice(0, 200) + '...' : plainText;
    }

    // Swatches — populate if product has color option
    if (this.swatchesWrap) {
      const colorOption = product.options?.find((o) => o.name.toLowerCase() === 'color' || o.name.toLowerCase() === 'scent');
      if (colorOption && colorOption.values.length > 1) {
        this.swatchesWrap.hidden = false;
        this.swatchesWrap.innerHTML = `
          <p class="hs-upsell-modal__swatch-label">CHOOSE YOUR ${this.#escapeHtml(colorOption.name.toUpperCase())}</p>
          <div class="hs-upsell-modal__swatch-row">
            ${colorOption.values.map((val, i) => {
              const matchingVariant = product.variants.find((v) =>
                v.title.toLowerCase().includes(val.toLowerCase())
              );
              return `<button
                class="hs-upsell-swatch${i === 0 ? ' active' : ''}"
                data-variant-id="${matchingVariant?.id || variant?.id}"
                data-value="${this.#escapeHtml(val)}"
                aria-label="${this.#escapeHtml(val)}"
                style="background-color: ${this.#guessSwatchColor(val)};"
              ></button>`;
            }).join('')}
          </div>
          <p class="hs-upsell-modal__swatch-name">${this.#escapeHtml(colorOption.values[0])}</p>
        `;

        this.swatchesWrap.querySelectorAll('.hs-upsell-swatch').forEach((swatch) => {
          swatch.addEventListener('click', () => {
            this.swatchesWrap.querySelectorAll('.hs-upsell-swatch').forEach((s) => s.classList.remove('active'));
            swatch.classList.add('active');
            this.#upsellVariantId = parseInt(swatch.dataset.variantId, 10);
            const nameEl = this.swatchesWrap.querySelector('.hs-upsell-modal__swatch-name');
            if (nameEl) nameEl.textContent = swatch.dataset.value;
          });
        });
      } else {
        this.swatchesWrap.hidden = true;
        this.swatchesWrap.innerHTML = '';
      }
    }

    // Add button text
    if (this.addBtn) {
      this.addBtn.textContent = `Add \u2013 ${price}`;
    }
  }

  #showModal() {
    if (this.#dialog && typeof this.#dialog.showModal === 'function') {
      this.#dialog.showModal();
    }
  }

  #closeModal() {
    if (this.#dialog && typeof this.#dialog.close === 'function') {
      this.#dialog.close();
    }
  }

  #onAdd = async () => {
    if (!this.#upsellVariantId) return;

    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.textContent = 'Adding...';
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.#upsellVariantId, quantity: 1 }] }),
      });

      if (res.ok) {
        this.#closeModal();
        this.#openDrawer();
        // Dispatch add event so cart UI refreshes
        document.dispatchEvent(new CartAddEvent());
      }
    } catch {
      // Silently fail
    } finally {
      if (this.addBtn) {
        this.addBtn.disabled = false;
      }
    }
  };

  #onSkip = () => {
    this.#closeModal();
    this.#openDrawer();
  };

  #onDialogClose = () => {
    if (this.#pendingDrawerOpen) {
      this.#openDrawer();
    }
  };

  #openDrawer() {
    this.#pendingDrawerOpen = false;
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else if (drawer) {
      drawer.showDialog?.();
    }
  }

  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /**
   * Best-effort swatch color from name.
   * Falls back to a neutral gray.
   */
  #guessSwatchColor(name) {
    const colors = {
      'santal & vetiver': '#C5B9A8',
      'bergamot & hinoki': '#D4C9A8',
      'black rose & oud': '#7D5A5A',
      'eucalyptus': '#A8C5A8',
      'lavender': '#B8A9C9',
      'sandalwood': '#C5A882',
      'grapefruit': '#F0B8A0',
      'coconut': '#F5F0E0',
    };
    return colors[name.toLowerCase()] || '#D0D0D0';
  }
}

if (!customElements.get('upsell-modal-component')) {
  customElements.define('upsell-modal-component', UpsellModalComponent);
}
